import PropTypes from "prop-types";
import React, { useState } from "react";

import styled from "styled-components/macro";

import { getSignupErrorMessage } from "Signups/SharedComponents/getSignupErrorMessage";
import { signupItemStateHooks } from "Signups/SignupView/SignupSheet/Screens/SignupItems/stateHooks.js";
import { useGetSignupListType } from "Signups/SignupView/utils/useGetSignupListType";
import { useSignups } from "Signups/state";

import FnrButton3 from "UI/FnrButton3";
import { SharedResizing } from "UI/SharedResizingArea3";
import { useSharedResizing } from "UI/SharedResizingArea3";

import { formatAmountInCentsForDisplay } from "Utilities/Number/formatAmountInCentsForDisplay";

import { Content, P, RowBetween, Wrapper } from "./Components";
import { personType } from "./personType";
import removeFromSignup from "./removeFromSignup";

RemoveWithRefund.proptype = {
  usersToRemove: PropTypes.string,
  refundAmount: PropTypes.number,
  closeModal: PropTypes.func,
  displayName: PropTypes.string,
  currentSignupItem: PropTypes.shape({
    ID: PropTypes.number,
    name: PropTypes.string,
  }),
  removalType: PropTypes.oneOf([
    personType.USER,
    personType.HOUSEHOLD_MEMBER,
    personType.ADDITIONAL_NAME,
    "slot",
  ]),
  additionalName: PropTypes.any,
  user: PropTypes.any,
  household: PropTypes.any,
  signupListID: PropTypes.number,
};

export default function RemoveWithRefund({
  usersToRemove,
  refundAmount,
  closeModal,
  currentSignupItem,
  displayName,
  removalType,
  additionalName,
  user,
  household,
  signupListID,
  setError,
  updatedNumberOfSlots,
}) {
  const [removeWithRefund, setRemoveWithRefund] = React.useState(false);

  return (
    <SharedResizing>
      <Screen1
        key={1}
        displayName={displayName}
        usersToRemove={usersToRemove}
        setRemoveWithRefund={setRemoveWithRefund}
        currentSignupItem={currentSignupItem}
        refundAmount={refundAmount}
      />

      <Screen2
        key={2}
        displayName={displayName}
        usersToRemove={usersToRemove}
        closeModal={closeModal}
        currentSignupItem={currentSignupItem}
        removeWithRefund={removeWithRefund}
        setRemoveWithRefund={setRemoveWithRefund}
        refundAmount={refundAmount}
        removalType={removalType}
        user={user}
        household={household}
        additionalName={additionalName}
        signupListID={signupListID}
        setError={setError}
        updatedNumberOfSlots={updatedNumberOfSlots}
      />
    </SharedResizing>
  );
}

const Screen1 = ({
  displayName,
  usersToRemove,
  setRemoveWithRefund,
  currentSignupItem,
  refundAmount,
}) => {
  const { updateKey } = useSharedResizing();
  const { isItemType } = useGetSignupListType();

  const handleClickRemoveWithRefund = () => {
    setRemoveWithRefund(true);
    updateKey(2);
  };

  const handleClickRemoveWithoutRefund = () => {
    setRemoveWithRefund(false);
    updateKey(2);
  };

  const usersToRemoveCount = usersToRemove.length;
  const itemTypeAddAnS =
    usersToRemoveCount > 1 && !currentSignupItem?.name?.endsWith("s");

  const paragraphText = isItemType ? (
    <P>
      You are removing{" "}
      <Bold>
        {usersToRemoveCount} {currentSignupItem?.name}
        {itemTypeAddAnS ? "s" : ""}
      </Bold>{" "}
      for {displayName}.
    </P>
  ) : (
    <P>
      You are removing <Bold>{displayName}</Bold> from{" "}
      <Bold>{currentSignupItem?.name}</Bold>.
    </P>
  );

  const refundText = !isItemType
    ? "this signup"
    : usersToRemoveCount > 1
    ? "these items"
    : "this item";

  const filteredUsersToRemove = usersToRemove.filter(
    (user) => user.trim() !== displayName
  );
  const othersToRemove = filteredUsersToRemove.reduce((acc, cur, i) => {
    let trimmedCur = cur.trim();
    if (i === 0) return trimmedCur;
    if (usersToRemove?.length === 2 && i === 1)
      return `${acc} and ${trimmedCur}`;
    if (i === filteredUsersToRemove?.length - 1)
      return `${acc}, and ${trimmedCur}`;
    return `${acc}, ${trimmedCur}`;
  }, "");

  let width = window.innerWidth < 580 ? window.innerWidth : "580px";

  return (
    <Wrapper style={{ width: width }}>
      <Content>
        <AltHeading>{paragraphText}</AltHeading>

        {othersToRemove.length > 0 && !isItemType ? (
          <P>
            <Bold>{othersToRemove}</Bold> will also be automatically removed.
          </P>
        ) : null}

        <P>
          Do you want to refund the{" "}
          <Bold>${formatAmountInCentsForDisplay(refundAmount)}</Bold> they paid
          for {refundText}?
        </P>

        <RowBetween style={{ marginTop: "1rem" }}>
          <FnrButton3
            onClick={handleClickRemoveWithoutRefund}
            color="red"
            style={width < 580 ? { width: "100%" } : null}
          >
            Remove without refund
          </FnrButton3>
          <FnrButton3
            color="green"
            onClick={handleClickRemoveWithRefund}
            style={width < 580 ? { width: "100%" } : null}
          >
            Remove and refund ${formatAmountInCentsForDisplay(refundAmount)}
          </FnrButton3>
        </RowBetween>
      </Content>
    </Wrapper>
  );
};

const Screen2 = ({
  displayName,
  usersToRemove,
  closeModal,
  currentSignupItem,
  removeWithRefund,
  setRemoveWithRefund,
  refundAmount,
  removalType,
  user,
  household,
  additionalName,
  signupListID,
  setError,
  updatedNumberOfSlots,
}) => {
  const [, dispatch] = useSignups();
  const { updateKey } = useSharedResizing();
  const usersToRemoveCount = usersToRemove.length;
  const { isItemType } = useGetSignupListType();
  const { getSignupItem } = signupItemStateHooks.useGetSignupItem();
  const [isLoading, setIsLoading] = useState(false);

  const handleRemoveWithRefund = () => {
    handleRemove({ shouldRefund: true });
  };

  const handleRemoveWithoutRefund = () => {
    handleRemove({ shouldRefund: false });
  };

  const handleRemove = ({ shouldRefund }) => {
    // console.log("userType", userType);
    // send request to API, then close modal
    let postData;

    if (isItemType && removalType === "slot") {
      // console.log("item-type");
      postData = {
        signupListID: signupListID,
        signupItemID: currentSignupItem.ID,
        userID: user.userID,
        numberOfSlots: updatedNumberOfSlots,
        signedUpViaID: 5, // 5 indicates updated by an admin
        shouldRefund: shouldRefund,
        refundAmount,
      };
    } else if (removalType === personType.ADDITIONAL_NAME) {
      // console.log("additional name");
      postData = {
        additionalNameID: additionalName.additionalNameID,
        signupItemID: currentSignupItem.ID,
        signedUpViaID: 5,
        shouldRefund: shouldRefund,
        refundAmount,
      };
    } else if (removalType === personType.HOUSEHOLD_MEMBER) {
      // console.log("household member");
      postData = {
        signupItemID: currentSignupItem.ID,
        userID: user.userID,
        signedUpViaID: 5,
        shouldRefund: shouldRefund,
        refundAmount,
      };
    } else if (removalType === personType.USER) {
      // console.log("user");
      postData = {
        signupItemID: currentSignupItem.ID,
        userID: user.userID,
        signedUpViaID: 5,
        shouldRefund: shouldRefund,
        refundAmount,
      };
    } else if (removalType === personType.HOUSEHOLD) {
      // console.log("household");
      postData = {
        signupItemID: currentSignupItem.ID,
        householdID: household.householdID,
        signedUpViaID: 5,
        shouldRefund: shouldRefund,
        refundAmount,
      };
    }

    // console.log("postData", postData);
    setIsLoading(true);
    removeFromSignup({
      signupID: signupListID,
      postData,
      removalType,
      isItemType,
    })
      .then((res) => {
        if (postData?.signupItemID) {
          getSignupItem(postData.signupItemID);
        }
        dispatch({
          type: "Other User Signup Updated",
          signupList: res.signupList,
          allSignupLists: res.allSignupLists,
        });

        setIsLoading(false);
        closeModal();
      })
      .catch((err) => {
        // console.log("err", err);
        let message = getSignupErrorMessage(err.error);
        setError(message);
        setIsLoading(false);
      });
  };

  const handleBack = () => {
    updateKey(1);
    setRemoveWithRefund(false);
  };

  const paragraphText = isItemType ? (
    <P>
      Are you sure you want to remove{" "}
      <Bold>
        {usersToRemoveCount} {currentSignupItem?.name}
        {usersToRemoveCount > 1 ? "s" : ""}
      </Bold>{" "}
      for {displayName}
      {removeWithRefund && (
        <>
          {" "}
          and refund them{" "}
          <Bold>${formatAmountInCentsForDisplay(refundAmount)}</Bold>
        </>
      )}
      ?
    </P>
  ) : (
    <P>
      Are you sure you want to remove <Bold>{displayName}</Bold>
      {removeWithRefund && (
        <>
          {" "}
          and refund them{" "}
          <Bold>${formatAmountInCentsForDisplay(refundAmount)}</Bold>
        </>
      )}
      ?
    </P>
  );

  const filteredUsersToRemove = usersToRemove.filter(
    (user) => user.trim() !== displayName
  );
  const othersToRemove = filteredUsersToRemove.reduce((acc, cur, i) => {
    let trimmedCur = cur.trim();
    if (i === 0) return trimmedCur;
    if (usersToRemove?.length === 2 && i === 1)
      return `${acc} and ${trimmedCur}`;
    if (i === filteredUsersToRemove?.length - 1)
      return `${acc}, and ${trimmedCur}`;
    return `${acc}, ${trimmedCur}`;
  }, "");

  let width = window.innerWidth < 460 ? window.innerWidth : "450px";

  return (
    <Wrapper style={{ width: width }}>
      <Content>
        <AltHeading>{paragraphText}</AltHeading>

        {othersToRemove.length > 0 && !isItemType ? (
          <P>
            <Bold>{othersToRemove}</Bold> will also be automatically removed.
          </P>
        ) : null}

        <RowBetween style={{ marginTop: "1rem" }}>
          <FnrButton3
            onClick={handleBack}
            style={width < 580 ? { width: "34%" } : null}
          >
            <i
              className="fas fa-chevron-left"
              style={{ marginRight: "8px", fontSize: ".9rem" }}
            ></i>
            Back
          </FnrButton3>
          {removeWithRefund ? (
            <FnrButton3
              color="green"
              onClick={handleRemoveWithRefund}
              style={width < 580 ? { width: "100%" } : null}
              loading={isLoading}
            >
              Remove and refund ${formatAmountInCentsForDisplay(refundAmount)}
            </FnrButton3>
          ) : (
            <FnrButton3
              color="red"
              onClick={handleRemoveWithoutRefund}
              style={width < 580 ? { width: "100%" } : null}
              loading={isLoading}
            >
              {`Remove, but don't refund`}
            </FnrButton3>
          )}
        </RowBetween>
      </Content>
    </Wrapper>
  );
};

const AltHeading = styled.div`
  font-size: 1.35rem;
`;
const Bold = styled.span`
  font-weight: 600;
`;
